import { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  User, 
  UtensilsCrossed, 
  ShoppingBag, 
  Settings, 
  HelpCircle, 
  Menu,
  X,
  ChevronRight,
  BarChart3,
  Users,
  LogOut,
  ChevronDown,
  Building2,
  Bot,
  Coffee,
  Link as LinkIcon,
  TrendingDown,
  ChefHat,
  Activity,
  Database,
  Wrench,
  Volume2,
  VolumeX,
  Globe,
  Gift
} from 'lucide-react';
import { useApp, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';

const navItems = [
  { path: '/admin/profile', label: 'Профиль', icon: User },
  { path: '/admin/menu', label: 'Меню', icon: UtensilsCrossed },
  { path: '/admin/orders', label: 'Заказы', icon: ShoppingBag },
  // Analytics group rendered separately (collapsible)
  { path: '/admin/settings', label: 'Настройки', icon: Settings },
  { path: '/admin/help', label: 'Справочный центр', icon: HelpCircle },
];

const analyticsItems = [
  { path: '/admin/analytics', label: 'Сводка', icon: BarChart3, exact: true },
  { path: '/admin/factual-margin', label: 'Факт. маржа', icon: Activity },
  { path: '/admin/recipes', label: 'Калькуляция блюд', icon: ChefHat },
  { path: '/admin/price-control', label: 'Контроль цен', icon: TrendingDown },
  { path: '/admin/caffesta?tab=analytics', label: 'Аналитика POS', icon: Coffee, matchPath: '/admin/caffesta', matchTab: 'analytics' },
  { path: '/admin/caffesta?tab=time-window', label: 'Сравнение по времени', icon: Activity, matchPath: '/admin/caffesta', matchTab: 'time-window' },
  { path: '/admin/caffesta?tab=report', label: 'Реализация', icon: BarChart3, matchPath: '/admin/caffesta', matchTab: 'report' },
];

const systemItems = [
  { path: '/admin/telegram-bot', label: 'Telegram-бот', icon: Bot },
  { path: '/admin/caffesta', label: 'Caffesta POS', icon: Coffee },
  { path: '/admin/caffesta-mapping', label: 'Маппинг Caffesta', icon: LinkIcon },
  { path: '/admin/loyalty', label: 'Лояльность', icon: Gift },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('ws_sound') !== 'off');
  const [unreadCount, setUnreadCount] = useState(0);
  const { restaurant, restaurants, currentRestaurantId, switchRestaurant, user, handleLogout, token } = useApp();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const audioRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioRef.current) {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioRef.current = ctx;
      }
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch { /* audio not available */ }
  }, [soundEnabled]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'new_order') {
      const order = msg.data;
      playNotificationSound();
      setUnreadCount(c => c + 1);
      if (order.is_preorder) {
        toast.info(`Новый предзаказ от ${order.customer_name || 'Гость'}`, {
          description: `${order.items?.length || 0} поз. — ${order.total?.toFixed(2)} ${restaurant?.currency || 'BYN'}`,
          action: { label: 'Открыть', onClick: () => navigate('/admin/orders') },
          duration: 8000,
        });
      } else {
        toast.info(`Новый заказ — Стол #${order.table_number}`, {
          description: `${order.items?.length || 0} поз. — ${order.total?.toFixed(2)} ${restaurant?.currency || 'BYN'}`,
          action: { label: 'Открыть', onClick: () => navigate('/admin/orders') },
          duration: 8000,
        });
      }
      window.dispatchEvent(new CustomEvent('ws:new_order', { detail: order }));
    } else if (msg.type === 'new_staff_call') {
      const call = msg.data;
      playNotificationSound();
      setUnreadCount(c => c + 1);
      toast.warning(`Вызов — Стол #${call.table_number}`, {
        description: call.call_type_name || 'Вызов персонала',
        action: { label: 'Открыть', onClick: () => navigate('/admin/orders') },
        duration: 8000,
      });
      window.dispatchEvent(new CustomEvent('ws:new_staff_call', { detail: call }));
    }
  }, [playNotificationSound, navigate]);

  const { connected } = useWebSocket(currentRestaurantId, token, handleWsMessage);

  // Reset unread count when on orders page
  useEffect(() => {
    if (location.pathname === '/admin/orders') setUnreadCount(0);
  }, [location.pathname]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('ws_sound', next ? 'on' : 'off');
    toast.success(next ? 'Звук уведомлений включён' : 'Звук уведомлений выключен');
  };

  const currentRestaurant = restaurants.find(r => r.id === currentRestaurantId) || restaurant;
  const enabledModules = currentRestaurant?.enabled_modules || [];
  const isModuleEnabled = (mod) => user?.role === 'superadmin' || enabledModules.includes(mod);

  // Filter analytics items by feature-flags
  const visibleAnalyticsItems = analyticsItems.filter((it) => {
    if (it.path === '/admin/analytics') return true; // Сводка всегда
    if (it.path === '/admin/factual-margin') return isModuleEnabled('factual_margin');
    if (it.path === '/admin/price-control') return isModuleEnabled('cost_control');
    if (it.path === '/admin/recipes') return isModuleEnabled('cost_control');
    if (it.matchPath === '/admin/caffesta') return isModuleEnabled('caffesta');
    return true;
  });

  // Filter system items by feature-flags
  const visibleSystemItems = systemItems.filter((it) => {
    if (it.path === '/admin/telegram-bot') return isModuleEnabled('telegram_bot');
    if (it.path === '/admin/caffesta') return isModuleEnabled('caffesta');
    if (it.path === '/admin/caffesta-mapping') return isModuleEnabled('caffesta_mapping');
    if (it.path === '/admin/loyalty') return isModuleEnabled('loyalty');
    return true;
  });

  // Analytics group: open by default if active route is one of analytics items
  const analyticsPaths = ['/admin/analytics', '/admin/factual-margin', '/admin/price-control', '/admin/recipes'];
  const currentTab = new URLSearchParams(location.search).get('tab');
  const analyticsActive =
    analyticsPaths.includes(location.pathname) ||
    (location.pathname === '/admin/caffesta' && ['analytics', 'time-window', 'report'].includes(currentTab));
  const [analyticsOpen, setAnalyticsOpen] = useState(analyticsActive);
  useEffect(() => { if (analyticsActive) setAnalyticsOpen(true); }, [analyticsActive]);

  // System group: open by default if active route is one of system items
  const systemPaths = [...systemItems.map(i => i.path), '/admin/backup'];
  const systemActive =
    systemPaths.includes(location.pathname) &&
    !(location.pathname === '/admin/caffesta' && ['analytics', 'time-window', 'report'].includes(currentTab));
  const [systemOpen, setSystemOpen] = useState(systemActive);
  useEffect(() => { if (systemActive) setSystemOpen(true); }, [systemActive]);

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed md:static inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col transition-transform duration-300 md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo/Header with Restaurant Selector */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 min-w-0">
              {currentRestaurant?.logo_url ? (
                <img
                  src={currentRestaurant.logo_url}
                  alt={currentRestaurant.name || 'Logo'}
                  className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                  data-testid="brand-logo"
                />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-mint-500 flex items-center justify-center flex-shrink-0">
                  <UtensilsCrossed className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-heading font-semibold truncate" data-testid="brand-name">
                  {currentRestaurant?.name || 'Личный кабинет'}
                </p>
                {currentRestaurant?.slogan && (
                  <p className="text-xs text-muted-foreground truncate">{currentRestaurant.slogan}</p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Restaurant Selector */}
          {restaurants.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full justify-between h-auto py-2"
                  data-testid="restaurant-selector"
                >
                  <div className="flex items-center gap-2 text-left">
                    <Building2 className="w-4 h-4 text-mint-500 flex-shrink-0" />
                    <span className="truncate font-medium">{currentRestaurant?.name || 'Выберите ресторан'}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[240px]">
                {restaurants.map((r) => (
                  <DropdownMenuItem 
                    key={r.id} 
                    onClick={() => switchRestaurant(r.id)}
                    className={cn(
                      "cursor-pointer",
                      r.id === currentRestaurantId && "bg-mint-500 text-white focus:bg-mint-600 focus:text-white data-[highlighted]:bg-mint-600 data-[highlighted]:text-white"
                    )}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    {r.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
              <Building2 className="w-4 h-4 text-mint-500" />
              <span className="font-medium truncate">{currentRestaurant?.name || 'Ресторан'}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            const navLink = (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  isActive 
                    ? "bg-mint-500 text-white shadow-lg shadow-mint-500/30" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
                data-testid={`nav-${item.path.split('/').pop()}`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {item.path === '/admin/orders' && unreadCount > 0 && !isActive && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white animate-pulse" data-testid="unread-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </NavLink>
            );

            // Insert Analytics collapsible group right before "Настройки"
            if (item.path === '/admin/settings') {
              return (
                <div key="analytics-wrap">
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => setAnalyticsOpen(o => !o)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                        analyticsActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                      data-testid="nav-analytics-toggle"
                      aria-expanded={analyticsOpen}
                    >
                      <BarChart3 className="w-5 h-5" />
                      <span className="font-medium">Аналитика</span>
                      <ChevronDown className={cn("w-4 h-4 ml-auto transition-transform", analyticsOpen ? "rotate-180" : "")} />
                    </button>
                    {analyticsOpen && (
                      <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                        {visibleAnalyticsItems.map((ai) => {
                          const AIcon = ai.icon;
                          let aActive;
                          if (ai.matchPath) {
                            aActive = location.pathname === ai.matchPath && currentTab === ai.matchTab;
                          } else if (ai.exact) {
                            aActive = location.pathname === ai.path && !currentTab;
                          } else {
                            aActive = location.pathname === ai.path;
                          }
                          return (
                            <NavLink
                              key={ai.path}
                              to={ai.path}
                              onClick={() => setSidebarOpen(false)}
                              className={cn(
                                "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm",
                                aActive
                                  ? "bg-mint-500 text-white shadow-md shadow-mint-500/20"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              )}
                              data-testid={`nav-analytics-${ai.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            >
                              <AIcon className="w-4 h-4" />
                              <span className="font-medium">{ai.label}</span>
                              {aActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {navLink}
                </div>
              );
            }

            return navLink;
          })}

          {/* System section (collapsible) — superadmin (всегда) + administrator (если есть включённые модули) */}
          {(user?.role === 'superadmin' || (user?.role === 'administrator' && visibleSystemItems.length > 0)) && (
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setSystemOpen(o => !o)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                systemActive
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-system-toggle"
              aria-expanded={systemOpen}
            >
              <Wrench className="w-5 h-5" />
              <span className="font-medium">Системные</span>
              <ChevronDown
                className={cn(
                  "w-4 h-4 ml-auto transition-transform",
                  systemOpen ? "rotate-180" : ""
                )}
              />
            </button>
            {systemOpen && (
              <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
                {visibleSystemItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm",
                        isActive
                          ? "bg-mint-500 text-white shadow-md shadow-mint-500/20"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                      data-testid={`nav-${item.path.split('/').pop()}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </NavLink>
                  );
                })}
                {/* Backup link inside Системные — superadmin only */}
                {user?.role === 'superadmin' && (
                  <NavLink
                    to="/admin/backup"
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm",
                      location.pathname === '/admin/backup'
                        ? "bg-mint-500 text-white shadow-md shadow-mint-500/20"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    data-testid="nav-backup"
                  >
                    <Database className="w-4 h-4" />
                    <span className="font-medium">Резервные копии</span>
                    {location.pathname === '/admin/backup' && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </NavLink>
                )}
              </div>
            )}
          </div>
          )}

          {/* Users + Restaurant modules (superadmin only) */}
          {user?.role === 'superadmin' && (
            <NavLink
              to="/admin/restaurant-modules"
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                location.pathname === '/admin/restaurant-modules'
                  ? "bg-mint-500 text-white shadow-lg shadow-mint-500/30"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-restaurant-modules"
            >
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Модули ресторанов</span>
              {location.pathname === '/admin/restaurant-modules' && <ChevronRight className="w-4 h-4 ml-auto" />}
            </NavLink>
          )}

          {/* Domains status (superadmin only) */}
          {user?.role === 'superadmin' && (
            <NavLink
              to="/admin/domains-status"
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                location.pathname === '/admin/domains-status'
                  ? "bg-mint-500 text-white shadow-lg shadow-mint-500/30"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-domains-status"
            >
              <Globe className="w-5 h-5" />
              <span className="font-medium">Статус доменов</span>
              {location.pathname === '/admin/domains-status' && <ChevronRight className="w-4 h-4 ml-auto" />}
            </NavLink>
          )}

          {/* Users link (superadmin only) */}
          {user?.role === 'superadmin' && (
            <NavLink
              to="/admin/users"
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                location.pathname === '/admin/users'
                  ? "bg-mint-500 text-white shadow-lg shadow-mint-500/30" 
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-users"
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Пользователи</span>
              {location.pathname === '/admin/users' && <ChevronRight className="w-4 h-4 ml-auto" />}
            </NavLink>
          )}
        </nav>

        {/* User info & Logout */}
        <div className="p-4 border-t border-border">
          {/* Connection status */}
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", connected ? "bg-green-500" : "bg-red-500")} data-testid="ws-status-sidebar" />
              <span className="text-xs text-muted-foreground">{connected ? 'Онлайн' : 'Нет связи'}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleSound} data-testid="toggle-sound-sidebar">
              {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-muted-foreground" />}
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-mint-100 dark:bg-mint-900/30 flex items-center justify-center">
              <User className="w-5 h-5 text-mint-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.username}</p>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'superadmin' ? 'Суперадмин' : user?.role === 'administrator' ? 'Администратор' : 'Менеджер'}
              </p>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="w-full justify-start text-muted-foreground hover:text-destructive hover:border-destructive"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Выйти
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 bg-card border-b border-border p-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <h1 className="font-heading font-bold truncate">{currentRestaurant?.name}</h1>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleSound} data-testid="toggle-sound-mobile">
              {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
            </Button>
            <div className={cn("w-2 h-2 rounded-full", connected ? "bg-green-500" : "bg-red-500")} data-testid="ws-status-mobile" title={connected ? 'Онлайн' : 'Нет связи'} />
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 p-4 md:p-8 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
